<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({ name: 'TransDetail' });
</script>
<!-- 流水详情弹窗组件（含优惠详情） -->
<template>
  <Popup
    v-model="visible"
    type="drawer"
    :title="title"
    :expand-data="computedDetailsList"
    :data-obj="dataObj"
    :label-config="labelConfig"
    :active-abil="activeAbil"
    :discount-table-is-refund="discountTableIsRefund"
    :close-text="closeText"
    :value-list="valueList"
    :drawer-size="drawerSize"
    :direction="direction"
    @close="handleClose"
  >
    <!-- 终端发起时间 -->
    <template #termDateTime="{ item, data }">
      <span>{{ data[item.value] ? convertTime(data[item.value]) : '/' }}</span>
    </template>

    <!-- 网关接收交易时间 -->
    <template #sysDateTime="{ item, data }">
      <span>{{ data[item.value] ? convertTime(data[item.value]) : '/' }}</span>
    </template>

    <!-- 支付时间 -->
    <template #payDateTime="{ item, data }">
      <span>{{ data[item.value] ? convertTime(data[item.value]) : '/' }}</span>
    </template>

    <!-- 卡品牌 -->
    <template #accountHolderBankType="{ item, data }">
      <span>{{ data[item.value] ? getBankName(data[item.value]) : '/' }}</span>
    </template>

    <!-- 币种 -->
    <template #currency="{ item, data }">
      <span>{{ getCurrencyDisplayName(data[item.value]) }}</span>
    </template>

    <!-- 优惠信息 -->
    <template #testfont1="{ data }">
      <span
        :style="data.isHaveDiscount == '1' ? 'color: #039fff;cursor: pointer;' : ''"
        @click="handleDiscountClick(data)"
      >
        {{ $t(data.isHaveDiscount == '1' ? 'hasDiscount' : 'noDiscount') }}
      </span>
    </template>

    <!-- 实付金额 -->
    <template #testfont2="{ data }">
      <span>
        {{ data.actualPaymentAmount ? data.actualPaymentAmount : '/' }}
        {{ data.actualPaymentCurrency ? '(' + data.actualPaymentCurrency + ')' : '' }}
      </span>
    </template>

    <!-- 渠道商户号脱敏 -->
    <template #outMid="{ item, data }">
      <span>{{ maskData(data[item.value]) }}</span>
    </template>

    <!-- 支付者标识脱敏 -->
    <template #userId="{ item, data }">
      <span>{{ maskData(data[item.value]) }}</span>
    </template>

    <!-- 透传外部自定义 slot -->
    <template v-for="name in customSlotNames" :key="name" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps" />
    </template>
  </Popup>

  <!-- 优惠详情弹窗（内置） -->
  <DiscountDetail
    v-model="dialogVisibleDiscount"
    :data="dataDiscountList"
    :is-refund="discountTableIsRefund"
    :close-text="closeText"
  />
</template>

<script setup lang="ts">
import { computed, ref, useSlots } from 'vue';
import { Popup } from '../../components/Popup';
import { DiscountDetail } from '../../components/DiscountDetail';
import { getDetailsByAbil } from './use-details-data';
import { convertTime } from '@klondpay/utils';

;

// ---------- 内置 slot 名称列表 ----------
const builtInSlots = [
  'accountHolderBankType',
  'currency',
  'testfont1',
  'testfont2',
  'outMid',
  'userId',
];

const props = defineProps({
  /** v-model 控制弹窗显隐 */
  modelValue: {
    type: Boolean,
    default: false,
  },
  /** 弹窗标题 */
  title: {
    type: String,
    default: '交易流水详情',
  },
  /** 展示数据列表（分组结构） */
  expandData: {
    type: Array,
    default: () => [],
  },
  /** 详情数据对象 */
  dataObj: {
    type: Object,
    default: () => ({}),
  },
  /** 标签提示配置 */
  labelConfig: {
    type: Object,
    default: () => ({}),
  },
  /** 当前业务类型 */
  activeAbil: {
    type: String,
    default: '',
  },
  /** 是否为退款场景 */
  discountTableIsRefund: {
    type: Boolean,
    default: false,
  },
  /** 关闭按钮文字 */
  closeText: {
    type: String,
    default: '关闭',
  },
  /** 特殊值字段列表 */
  valueList: {
    type: Array,
    default: () => [],
  },
  /** 抽屉尺寸 */
  drawerSize: {
    type: String,
    default: '60%',
  },
  /** 抽屉方向 */
  direction: {
    type: String,
    default: 'rtl',
  },
  /** 银行卡类型列表（用于卡品牌名称映射） */
  bankTypeList: {
    type: Array,
    default: () => [],
  },
  /** 币种列表（用于币种名称映射） */
  currencyList: {
    type: Array,
    default: () => [],
  },
  /**
   * 获取优惠列表的 API 函数
   * 签名: (params: { sysId: string }) => Promise<{ code: number; result: any[] }>
   * 传入后组件内部自动处理优惠详情弹窗，无需外部管理
   */
  discountApi: {
    type: Function,
    default: null,
  },
  /**
   * 金额格式化函数
   * 签名: (amount, currencyList, currency, type) => string
   * 用于格式化优惠数据中的金额字段
   */
  formatCurrencyFn: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'close', 'discount-click']);

// ---------- 根据业务类型动态获取详情字段列表 ----------
const computedDetailsList = computed(() => {
  return getDetailsByAbil(props.activeAbil);
});

// ---------- 双向绑定 visible ----------
const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

// ---------- 透传自定义 slot ----------
const slots = useSlots();
const customSlotNames = computed(() => {
  return Object.keys(slots).filter((name) => !builtInSlots.includes(name));
});

// ---------- 优惠详情内部状态 ----------
const dialogVisibleDiscount = ref(false);
const dataDiscountList = ref<Record<string, unknown>[]>([]);

// ---------- 内部方法 ----------

/** 根据银行卡 code 获取名称 */
function getBankName(code: string): string {
  const list = props.bankTypeList as { bankId: string; name: string }[];
  const matched = list.find((item) => item.bankId === code);
  return matched ? matched.name : code || '/';
}

/** 根据币种 code 获取名称 */
function getCurrencyDisplayName(targetCurrency: string): string {
  if (!targetCurrency) return '/';
  const list = props.currencyList as { value: string; label: string }[];
  const matched = list.find((item) => item.value === targetCurrency);
  return matched ? matched.label : '/';
}

/** 脱敏处理 */
function maskData(str: string | null | undefined): string {
  if (!str) return '/';
  const input = String(str);
  const length = input.length;
  const targetPrefix = 6;
  const targetSuffix = 5;
  const minRequired = targetPrefix + targetSuffix;
  if (length >= minRequired) {
    return `${input.slice(0, targetPrefix)}...${input.slice(-targetSuffix)}`;
  }
  if (length >= 6) {
    return `${input.slice(0, 3)}...${input.slice(-2)}`;
  }
  if (length >= 3) {
    return `${input.slice(0, 1)}...${input.slice(-1)}`;
  }
  return input;
}

/** 优惠信息点击 —— 如有 discountApi 则内部处理，否则向外 emit */
function handleDiscountClick(data: Record<string, unknown>) {
  if (props.discountApi && typeof props.discountApi === 'function') {
    // 内部处理优惠详情
    openDiscountInternal(data);
  } else {
    // 兼容旧用法：向外 emit
    emit('discount-click', data);
  }
}

/** 内部打开优惠详情弹窗 */
function openDiscountInternal(row: Record<string, unknown>) {
  if (row.isHaveDiscount !== '1') return;

  props.discountApi({ sysId: row.sysId }).then((res: Record<string, unknown>) => {
    if (res.code === 20000) {
      const result = res.result as Record<string, unknown>[];
      dataDiscountList.value = result.map((item: Record<string, unknown>) => {
        // 若提供了格式化函数则格式化金额
        if (props.formatCurrencyFn && typeof props.formatCurrencyFn === 'function') {
          item.amount = props.formatCurrencyFn(
            item.amount,
            props.currencyList,
            item.currency,
            'self',
          );
          item.refund_amount = props.formatCurrencyFn(
            item.refund_amount,
            props.currencyList,
            item.currency,
            'self',
          );
        }
        return item;
      });
      dialogVisibleDiscount.value = true;
    }
  });
}

/** 关闭处理 */
function handleClose() {
  emit('close');
}
</script>
